import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {RTC_PROTOCOL_V1, type ActiveRingInvite} from '@phonehq/rtc-core';
import {matrixService} from '../../../services/matrixService';
import logger from '../../../services/logger';
import {isServiceParticipant} from '../../../utils/aiBotUtils';
import {useRoomCalls, useRtcFeatures} from '../../groupCalls/hooks/useGroupCall';
import {countCallPeople} from '../../groupCalls/services/callPeople';
import {groupCallService} from '../../groupCalls/services/groupCallService';
import {isOneToOneRoom} from '../../groupCalls/services/roomCallEntry';
import {activeCallRows, type ActiveCallRow} from './activeCalls';

const POLL_MS = 15_000;

export type ActiveInvitesStatus = 'loading' | 'ready' | 'error';

const isListedRoom = (roomId: string): boolean => {
    const room = matrixService.getRoom(roomId);
    if (!room) {
        return false;
    }
    const joinedHumanMembers = room.getJoinedMembers().filter(member => !isServiceParticipant(member.userId)).length;
    return !isOneToOneRoom({isDirect: matrixService.isDirectMessageRoom(room), joinedHumanMembers});
};

export const useActiveCalls = (): {rows: ActiveCallRow[]; status: ActiveInvitesStatus; retry: () => void} => {
    const features = useRtcFeatures();
    const calls = useRoomCalls();
    const [invites, setInvites] = useState<readonly ActiveRingInvite[]>([]);
    const [status, setStatus] = useState<ActiveInvitesStatus>('loading');
    const mounted = useRef(true);
    const roomKey = [...calls.keys()].sort().join('|');

    const load = useCallback(() => {
        groupCallService.activeInvites()
            .then(next => {
                if (mounted.current) {
                    setInvites(next);
                    setStatus('ready');
                }
            })
            .catch(error => {
                logger.warn('Active call invites unavailable', {
                    source: 'group_call',
                    event: 'active_invites_failed',
                    error: error instanceof Error ? error.message : String(error),
                });
                if (mounted.current) {
                    setStatus('error');
                }
            });
    }, []);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!features.groupCalls) {
            return;
        }
        load();
        const timer = window.setInterval(load, POLL_MS);
        return () => window.clearInterval(timer);
    }, [features.groupCalls, load, roomKey]);

    const rows = useMemo(
        () => features.groupCalls
            ? activeCallRows({
                calls,
                invites,
                maxParticipants: RTC_PROTOCOL_V1.maxParticipants,
                isListed: isListedRoom,
                peopleIn: summary => countCallPeople(summary.userIds),
            })
            : [],
        [calls, features.groupCalls, invites],
    );

    const retry = useCallback(() => {
        setStatus('loading');
        load();
    }, [load]);

    return {rows, status: features.groupCalls ? status : 'ready', retry};
};
